import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sprout, Plus, Trash2, Save, Sparkles, Loader2, Mail, Send, UserPlus,
  Play, Pause, X, ChevronRight, Clock, Search, Eye, MousePointerClick,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { formatDateTime } from "../../lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  active: "bg-[#E3F3E9] text-[#0E6B3F] border-[#E3F3E9]",
  paused: "bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1]",
  completed: "bg-[#FFF8D6] text-[#8A6D00] border-[#FFF8D6]",
  unsubscribed: "bg-[#8E8E93] text-[#8E8E93] border-[#8E8E93]",
};

const blankStep = () => ({ day_offset: 0, subject: "", body_html: "", enabled: true });

export default function NurtureSequences() {
  const [sequences, setSequences] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);       // editable copy of the selected sequence
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiIdx, setAiIdx] = useState(null);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const loadSequences = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/nurture/sequences`);
      setSequences(r.data.sequences || []);
    } catch { toast.error("Failed to load sequences"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSequences(); }, [loadSequences]);

  const openSequence = async (id) => {
    setSelectedId(id);
    const r = await axios.get(`${API}/nurture/sequences/${id}`);
    setDraft(JSON.parse(JSON.stringify(r.data.sequence)));
    loadEnrollments(id);
  };

  const loadEnrollments = async (id) => {
    const r = await axios.get(`${API}/nurture/enrollments`, { params: { sequence_id: id } });
    setEnrollments(r.data.enrollments || []);
  };

  const newSequence = () => {
    setSelectedId("new");
    setDraft({ name: "", description: "", steps: [blankStep()], active: true });
    setEnrollments([]);
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Give the sequence a name");
    setSaving(true);
    try {
      if (selectedId === "new") {
        const r = await axios.post(`${API}/nurture/sequences`, draft);
        toast.success("Sequence created");
        await loadSequences();
        openSequence(r.data.sequence.id);
      } else {
        await axios.put(`${API}/nurture/sequences/${selectedId}`, draft);
        toast.success("Sequence saved");
        loadSequences();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (selectedId === "new") { setSelectedId(null); setDraft(null); return; }
    if (!window.confirm("Delete this sequence and all its enrollments?")) return;
    await axios.delete(`${API}/nurture/sequences/${selectedId}`);
    toast.success("Sequence deleted");
    setSelectedId(null); setDraft(null); loadSequences();
  };

  const updateStep = (i, patch) => setDraft((d) => ({
    ...d, steps: d.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s),
  }));
  const addStep = () => setDraft((d) => ({ ...d, steps: [...d.steps, blankStep()] }));
  const removeStep = (i) => setDraft((d) => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }));

  const aiDraftStep = async (i) => {
    const topic = window.prompt("What's the topic / angle for this email?\n(e.g. '3 signs your maintenance tracking is costing you money')");
    if (!topic) return;
    setAiIdx(i);
    try {
      const r = await axios.post(`${API}/nurture/draft`, { topic });
      updateStep(i, { subject: r.data.subject, body_html: r.data.body_html });
      toast.success("AI draft added — review & edit before saving");
    } catch (e) { toast.error(e.response?.data?.detail || "AI draft failed"); }
    finally { setAiIdx(null); }
  };

  const runNow = async () => {
    try {
      const r = await axios.post(`${API}/nurture/run-now`);
      toast.success(`Sent ${r.data.sent} due nurture email(s)`);
      if (selectedId && selectedId !== "new") loadEnrollments(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
  };

  const setEnrollmentStatus = async (eid, action) => {
    await axios.post(`${API}/nurture/enrollments/${eid}/${action}`);
    loadEnrollments(selectedId);
  };
  const unenroll = async (eid) => {
    await axios.delete(`${API}/nurture/enrollments/${eid}`);
    loadEnrollments(selectedId);
  };

  return (
    <div className="space-y-4" data-testid="nurture-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Sprout className="w-5 h-5 text-[#0E6B3F]" /> Nurture Sequences
          </h2>
          <p className="text-sm text-[#59554D] mt-1 max-w-2xl">
            Stay top of mind so prospects reach out when the timing is right for them. Build a
            sequence of value emails, then enrol prospects — each email goes out from their
            assigned rep and carries an unsubscribe link.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runNow}
            className="border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A]" data-testid="nurture-run-now">
            <Send className="w-4 h-4 mr-2" /> Send due now
          </Button>
          <Button size="sm" onClick={newSequence}
            className="bg-[#0E6B3F] text-black hover:bg-[#0E6B3F]" data-testid="nurture-new-sequence">
            <Plus className="w-4 h-4 mr-2" /> New sequence
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sequence list */}
        <div className="space-y-2" data-testid="nurture-sequence-list">
          {loading && <div className="text-[#8A857A] text-sm py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
          {!loading && sequences.length === 0 && (
            <Card className="bg-[#F4F2ED] border-dashed border-[#DAD5CA]">
              <CardContent className="p-6 text-center text-sm text-[#8A857A]">
                No sequences yet. Create your first nurture sequence.
              </CardContent>
            </Card>
          )}
          {sequences.map((s) => (
            <Card key={s.id}
              className={`bg-[#F4F2ED] border cursor-pointer transition-colors ${selectedId === s.id ? "border-[#0E6B3F]" : "border-[#DAD5CA] hover:border-[#DAD5CA]"}`}
              onClick={() => openSequence(s.id)} data-testid={`nurture-seq-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A1A1A] truncate">{s.name}</div>
                    <div className="text-xs text-[#8A857A] mt-0.5">
                      {(s.steps || []).length} step{(s.steps || []).length === 1 ? "" : "s"} · {s.active_enrollments || 0} enrolled
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!s.active && <Badge variant="outline" className="border-[#DAD5CA] text-[#8A857A] text-xs">Paused</Badge>}
                    <ChevronRight className="w-4 h-4 text-[#8A857A]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          {!draft ? (
            <Card className="bg-[#F4F2ED] border-[#DAD5CA] h-full">
              <CardContent className="p-10 text-center text-sm text-[#8A857A]">
                Select a sequence to edit, or create a new one.
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Sequence name (e.g. Monthly Value Newsletter)"
                    className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="nurture-name-input" />
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-[#59554D]">Active</span>
                    <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} data-testid="nurture-active-switch" />
                  </div>
                </div>
                <Input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Short description (optional)" className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm" />

                {/* Steps */}
                <div className="space-y-3">
                  {draft.steps.map((st, i) => (
                    <div key={i} className="border border-[#DAD5CA] rounded-lg p-3 space-y-2 bg-[#FFFFFF]" data-testid={`nurture-step-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs text-[#59554D]">
                          <Clock className="w-3.5 h-3.5 text-[#8A6D00]" /> Send on day
                          <Input type="number" value={st.day_offset}
                            onChange={(e) => updateStep(i, { day_offset: parseInt(e.target.value || "0", 10) })}
                            className="w-16 h-7 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-xs" data-testid={`nurture-step-day-${i}`} />
                          <span className="text-[#8A857A]">after enrolment</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => aiDraftStep(i)} disabled={aiIdx === i}
                            className="h-7 text-[#B794F6] hover:text-[#D6BCFA]" data-testid={`nurture-step-ai-${i}`}>
                            {aiIdx === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            <span className="ml-1 text-xs">AI draft</span>
                          </Button>
                          <Switch checked={st.enabled} onCheckedChange={(v) => updateStep(i, { enabled: v })} />
                          <Button variant="ghost" size="icon" onClick={() => removeStep(i)}
                            className="h-7 w-7 text-[#C23A00] hover:text-[#ff6b62]" data-testid={`nurture-step-remove-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <Input value={st.subject} onChange={(e) => updateStep(i, { subject: e.target.value })}
                        placeholder="Subject line" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-sm" data-testid={`nurture-step-subject-${i}`} />
                      <Textarea value={st.body_html} onChange={(e) => updateStep(i, { body_html: e.target.value })}
                        placeholder="Email body (HTML). Use {{first_name}} and {{company_name}}."
                        rows={4} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-sm font-mono" data-testid={`nurture-step-body-${i}`} />
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addStep}
                    className="border-dashed border-[#DAD5CA] text-[#59554D] w-full" data-testid="nurture-add-step">
                    <Plus className="w-4 h-4 mr-2" /> Add step
                  </Button>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <Button variant="ghost" size="sm" onClick={remove}
                    className="text-[#C23A00] hover:text-[#ff6b62]" data-testid="nurture-delete-sequence">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                  <Button size="sm" onClick={save} disabled={saving}
                    className="bg-[#FFD108] text-[#1A1A1A] hover:bg-[#0070e0]" data-testid="nurture-save-sequence">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
                  </Button>
                </div>

                {/* Enrollments */}
                {selectedId !== "new" && (
                  <div className="border-t border-[#DAD5CA] pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-[#1A1A1A]">Enrolled prospects ({enrollments.length})</h4>
                      <Button size="sm" variant="outline" onClick={() => setEnrollOpen(true)}
                        className="border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A] h-8" data-testid="nurture-enroll-open">
                        <UserPlus className="w-4 h-4 mr-2" /> Enrol prospects
                      </Button>
                    </div>
                    {enrollments.length === 0 ? (
                      <div className="text-xs text-[#8A857A] py-3">No one enrolled yet.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {enrollments.map((e) => (
                          <div key={e.id} className="flex items-center justify-between gap-2 text-xs bg-[#FFFFFF] border border-[#DAD5CA] rounded px-3 py-2"
                            data-testid={`nurture-enrollment-${e.id}`}>
                            <div className="min-w-0">
                              <span className="text-[#1A1A1A]">{e.company_name || e.prospect_id}</span>
                              <span className="text-[#8A857A] ml-2">
                                {e.status === "active" && e.next_send_at ? `next: ${e.next_send_at}` :
                                  e.last_sent_at ? `last: ${formatDateTime(e.last_sent_at)}` : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {(e.opens > 0 || e.clicks > 0) && (
                                <span className="flex items-center gap-2 text-[10px] mr-1" title="Nurture engagement">
                                  {e.opens > 0 && <span className="flex items-center gap-0.5 text-[#8A6D00]"><Eye className="w-3 h-3" />{e.opens}</span>}
                                  {e.clicks > 0 && <span className="flex items-center gap-0.5 text-[#6B5600]"><MousePointerClick className="w-3 h-3" />{e.clicks}</span>}
                                </span>
                              )}
                              <Badge className={`${STATUS_STYLE[e.status] || ""} text-[10px]`}>{e.status}</Badge>
                              {e.status === "active" && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-[#6B5600]" onClick={() => setEnrollmentStatus(e.id, "pause")} title="Pause">
                                  <Pause className="w-3 h-3" />
                                </Button>
                              )}
                              {e.status === "paused" && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-[#0E6B3F]" onClick={() => setEnrollmentStatus(e.id, "resume")} title="Resume">
                                  <Play className="w-3 h-3" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-[#8A857A] hover:text-[#C23A00]" onClick={() => unenroll(e.id)} title="Remove">
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {enrollOpen && (
        <EnrollDialog sequenceId={selectedId} onClose={() => setEnrollOpen(false)}
          onEnrolled={() => { setEnrollOpen(false); loadEnrollments(selectedId); loadSequences(); }} />
      )}
    </div>
  );
}

function EnrollDialog({ sequenceId, onClose, onEnrolled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sales/prospects`, { params: { search: term, limit: 25 } });
      const items = r.data.prospects || r.data.items || r.data || [];
      setResults(Array.isArray(items) ? items : []);
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { const t = setTimeout(() => search(q), 300); return () => clearTimeout(t); }, [q, search]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const count = Object.values(picked).filter(Boolean).length;

  const enroll = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/nurture/sequences/${sequenceId}/enroll`, { prospect_ids: ids });
      toast.success(`Enrolled ${r.data.enrolled}${r.data.skipped ? `, skipped ${r.data.skipped}` : ""}`);
      onEnrolled();
    } catch (e) { toast.error(e.response?.data?.detail || "Enrol failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="nurture-enroll-dialog">
        <DialogHeader><DialogTitle>Enrol prospects</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#8A857A]" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search prospects by company or contact…"
            className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] pl-9" data-testid="nurture-enroll-search" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1 mt-1">
          {loading && <div className="text-xs text-[#8A857A] py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Searching…</div>}
          {!loading && results.length === 0 && <div className="text-xs text-[#8A857A] py-4 text-center">No prospects found.</div>}
          {results.map((p) => (
            <label key={p.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[#FFFFFF] cursor-pointer" data-testid={`nurture-enroll-option-${p.id}`}>
              <input type="checkbox" checked={!!picked[p.id]} onChange={() => toggle(p.id)} className="accent-[#0E6B3F]" />
              <div className="min-w-0">
                <div className="text-sm text-[#1A1A1A] truncate">{p.company_name || "—"}</div>
                <div className="text-xs text-[#8A857A] truncate flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {p.contact_email || "no email"}
                  {p.nurture_unsubscribed && <span className="text-[#6B5600] ml-1">· unsubscribed</span>}
                </div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#59554D]">Cancel</Button>
          <Button onClick={enroll} disabled={busy || count === 0} className="bg-[#0E6B3F] text-black hover:bg-[#0E6B3F]" data-testid="nurture-enroll-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />} Enrol {count > 0 ? `(${count})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
